import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, UserPlus, LogIn, LogOut, ShoppingCart, CreditCard, ShieldCheck, ShieldX, AlertTriangle, UserCog, Building2 } from "lucide-react";

const ICONS = {
  "login.success":        { icon: LogIn,        cls: "text-emerald-600 bg-emerald-50" },
  "login.failed":         { icon: ShieldX,      cls: "text-red-600 bg-red-50" },
  "impersonate.start":    { icon: UserCog,      cls: "text-amber-600 bg-amber-50" },
  "organization.create":  { icon: Building2,    cls: "text-blue-600 bg-blue-50" },
  "organization.update":  { icon: Building2,    cls: "text-blue-600 bg-blue-50" },
  "organization.delete":  { icon: AlertTriangle,cls: "text-red-600 bg-red-50" },
  "team.invite":          { icon: UserPlus,     cls: "text-emerald-600 bg-emerald-50" },
  "team.remove":          { icon: AlertTriangle,cls: "text-amber-600 bg-amber-50" },
  "billing.upgrade":      { icon: CreditCard,   cls: "text-purple-600 bg-purple-50" },
  "billing.failed":       { icon: CreditCard,   cls: "text-red-600 bg-red-50" },
  "2fa.enable":           { icon: ShieldCheck,  cls: "text-emerald-600 bg-emerald-50" },
  "2fa.disable":          { icon: ShieldX,      cls: "text-amber-600 bg-amber-50" },
};

const LABELS = {
  "login.success":       "Geslaagde login",
  "login.failed":        "Mislukte login",
  "impersonate.start":   "Support inlogde als gebruiker",
  "organization.create": "Nieuwe organisatie aangemaakt",
  "organization.update": "Organisatie bijgewerkt",
  "organization.delete": "Organisatie verwijderd",
  "team.invite":         "Teamlid uitgenodigd",
  "team.remove":         "Teamlid verwijderd",
  "billing.upgrade":     "Plan geüpgrade",
  "billing.failed":      "Betaling mislukt",
  "2fa.enable":          "2FA ingeschakeld",
  "2fa.disable":         "2FA uitgeschakeld",
};

export default function LiveFeed({ compact = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/feed/live");
      setEvents(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh every 15s
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="text-sm text-zinc-500 py-6 text-center" data-testid="feed-loading">Laden…</div>;
  if (events.length === 0) return <div className="card-flat p-8 text-center text-sm text-zinc-500" data-testid="feed-empty">Geen recente activiteit in de afgelopen 24 uur.</div>;

  return (
    <div className={`card-flat p-4 ${compact ? "max-h-[400px] overflow-y-auto" : ""}`} data-testid="live-feed">
      <div className="flex items-center gap-2 mb-3">
        <Activity size={15} className="text-[#5BC044]" />
        <h3 className="font-bold tracking-tight text-[#1B2D5C] text-sm">Live activiteit</h3>
        <span className="ml-auto text-[10px] font-mono text-zinc-400">auto-refresh 15s</span>
      </div>
      <ol className="space-y-2">
        {events.map((e, i) => {
          const meta = ICONS[e.type] || { icon: Activity, cls: "text-zinc-600 bg-zinc-100" };
          const Icon = meta.icon;
          const when = e.at ? new Date(e.at).toLocaleString("nl-NL", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : "";
          return (
            <li key={i} className="flex items-start gap-3 py-2 px-1 hover:bg-zinc-50 rounded" data-testid={`feed-event-${i}`}>
              <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${meta.cls}`}>
                <Icon size={13} strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-semibold">{LABELS[e.type] || e.type}</span>
                  {e.actor && <span className="text-zinc-500"> · {e.actor}</span>}
                </div>
                {e.details && (
                  <div className="text-xs text-zinc-500 truncate mt-0.5">
                    {e.type === "login.success" && `${e.details.device || ""} · ${e.details.browser || ""} · ${e.details.ip || ""}`}
                    {e.type === "login.failed" && `${e.details.reason || "—"} · ${e.details.ip || ""}`}
                    {e.type === "impersonate.start" && `→ ${e.details.target_email || ""}`}
                    {e.type === "billing.upgrade" && `${e.details.plan || ""} · €${e.details.amount || 0}`}
                    {e.type === "team.invite" && `${e.details.email || ""} (${e.details.role || ""})`}
                    {e.type === "organization.create" && `${e.details.name || ""} · plan: ${e.details.plan || ""}`}
                  </div>
                )}
              </div>
              <span className="text-[10px] font-mono text-zinc-400 shrink-0 mt-1">{when}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
